import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { debounce } from 'lodash';
import { DeleteOutlined, LoginOutlined, MoreOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, Flex, Form, Input, Modal, Row, Select, Switch, Table } from 'antd';
import CollapsibleForm from '../../../components/form/general/CollapsibleForm';
import useForm from '../../../hooks/utility/useForm';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import useApiClient from '../../../hooks/api/useApiClient';
import useHelpers from '../../../hooks/context/useHelpers';
import usePermissions, { ManagerRole } from '../../../hooks/auth/usePermissions';

const DEFAULT_PER_PAGE = 15;

// Partner (non-master) providers may only staff their tenant with these roles —
// mirrors the whitelist enforced by the backend in CreateManagerRequest.
const PARTNER_GRANTABLE_ROLES = [ManagerRole.Admin, ManagerRole.OnboardingManager, ManagerRole.ContentManager];

export default function ManagersTab() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Team:Managers:ManagersTab' });

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { isSuperAdmin } = usePermissions();

  const { ManagersManager, MagicLinksManager } = useApiClient();

  const { formatDateTime } = useHelpers();

  const {
    data,
    update,
    reset: resetData,
  } = useForm({
    first_name: null,
    last_name: null,
    email: null,
    city: null,
    role: null,
  });

  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showDeleteManagerModal, setShowDeleteManagerModal] = useState(false);
  const [runningActionFor, setRunningActionForFor] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [total, setTotal] = useState(0);
  const { setFeedback, renderFeedbackLabel, resetFeedback } = useFeedbackLabel();

  useEffect(() => {
    loadManagers();
  }, [serviceProvider?.id, page, perPage, search]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setSearch(value);
      }, 400),
    [],
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const loadManagers = async () => {
    setLoading(true);
    const { success, data: responseData } = await ManagersManager.get({
      page,
      per_page: perPage,
      ...(search ? { search } : {}),
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    // Backend returns a Laravel paginator: { data: [...], total, current_page, ... }.
    // Guard against unexpected shapes so the Table never receives a non-array.
    const list = Array.isArray(responseData?.data) ? responseData.data : [];
    setManagers(list);
    setTotal(responseData?.total ?? list.length);
  };

  const createManager = async () => {
    setCreating(true);
    const { success, data: responseData } = await ManagersManager.create({
      ...data,
      roles: [data.role],
    });
    setCreating(false);

    if (!success) {
      setFeedback({
        message: responseData?.message,
        type: 'error',
        errors: responseData?.errors,
      });

      return toast.error(t('toasts.failed_to_create_manager'));
    }

    resetData();
    resetFeedback();

    return loadManagers();
  };

  const logInAs = async (manager) => {
    setRunningActionForFor(manager.id);
    const { success, data: linkData } = await MagicLinksManager.create({
      app: 'dashboard',
      manager_id: manager.id,
    });
    setRunningActionForFor(null);

    if (!success) {
      return toast.error(t('toasts.failed_to_create_magic_link'));
    }

    return window.location.replace(linkData.url);
  };

  const deleteManager = async (manager, requiresConfirmation = true) => {
    if (requiresConfirmation) {
      setSelectedManager(manager);

      return setShowDeleteManagerModal(true);
    }

    setRunningActionForFor(manager.id);
    const { success } = await ManagersManager.delete(manager.id);
    setRunningActionForFor(null);

    if (!success) {
      return toast.error(t('toasts.failed_to_delete_manager'));
    }

    toast.success(t('toasts.manager_got_deleted'));
    setShowDeleteManagerModal(false);
    setSelectedManager(null);

    return setManagers((current) => collect(current).where('id', '!=', manager.id).toArray());
  };

  const onStatusUpdate = async (manager, val) => {
    setRunningActionForFor(manager.id);

    return ManagersManager.partialUpdate(manager.id, { is_active: val })
      .then(({ success, data: updatedManager }) => {
        if (!success) {
          return toast.error(t('toasts.failed_to_update_manager'));
        }

        setManagers((prev) => {
          const index = prev.findIndex((item) => item.id === updatedManager.id);
          const result = [...prev];

          result[index] = updatedManager;

          return result;
        });
      })
      .finally(() => {
        setRunningActionForFor(null);
      });
  };

  const columns = [
    {
      title: t('fields.email'),
      key: 'email',
      dataIndex: 'email',
    },
    {
      title: t('fields.name'),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: t('fields.roles'),
      key: 'roles',
      render: (_, item) => collect(item.roles).implode(', '),
    },
    {
      title: t('fields.status'),
      key: 'is_active',
      render: (_, item) => (
        <Switch
          loading={runningActionFor === item.id}
          checked={item.is_active}
          hidden={item.roles.includes(ManagerRole.SuperAdmin)}
          onChange={(checked) => onStatusUpdate(item, checked)}
        />
      ),
    },
    {
      title: t('fields.created_at'),
      key: 'created_at',
      render: (_, item) => formatDateTime(item.created_at),
    },
    {
      title: t('fields.actions'),
      key: 'actions',
      render: (_, item) =>
        !item.roles.includes(ManagerRole.SuperAdmin) && (
          <Dropdown
            menu={{
              items: [
                // Impersonation stays super-admin-only: for other admins the
                // magic-link endpoint requires an acting-company context.
                ...(isSuperAdmin
                  ? [
                      {
                        key: 'view_dashboard',
                        label: t('buttons.view_dashboard'),
                        loading: runningActionFor === item.id,
                        icon: <LoginOutlined />,
                        onClick: () => logInAs(item),
                      },
                    ]
                  : []),
                {
                  key: 'delete',
                  label: t('buttons.delete'),
                  danger: true,
                  icon: <DeleteOutlined />,
                  onClick: () => deleteManager(item),
                },
              ],
            }}
          >
            <MoreOutlined />
          </Dropdown>
        ),
    },
  ];

  return (
    <>
      <CollapsibleForm title={t('form.title')} className="mt-md">
        <Form layout="vertical" className="mt-md">
          {renderFeedbackLabel}
          <Row gutter={8}>
            <Col xs={24} md={12}>
              <Form.Item label={t('form.first_name.label')}>
                <Input
                  value={data.first_name ?? ''}
                  onChange={(e) => update('first_name', e.target.value)}
                  placeholder={t('form.first_name.placeholder')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('form.last_name.label')}>
                <Input
                  value={data.last_name ?? ''}
                  onChange={(e) => update('last_name', e.target.value)}
                  placeholder={t('form.last_name.placeholder')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={t('form.city.label')}>
            <Input
              value={data.city ?? ''}
              onChange={(e) => update('city', e.target.value)}
              placeholder={t('form.city.placeholder')}
            />
          </Form.Item>
          <Form.Item label={t('form.email.label')}>
            <Input
              value={data.email ?? ''}
              onChange={(e) => update('email', e.target.value)}
              placeholder={t('form.email.placeholder')}
              type="email"
            />
          </Form.Item>
          <Form.Item label={t('form.roles.label')}>
            <Select
              value={data.role}
              onChange={(value) => update('role', value)}
              options={(serviceProvider?.is_master
                ? collect(Object.values(ManagerRole)).except([
                    ManagerRole.SuperAdmin,
                    ManagerRole.StoreAdmin,
                    ManagerRole.OccAgent,
                    ManagerRole.StoreOperator,
                    ManagerRole.StoreEmployee,
                  ])
                : collect(PARTNER_GRANTABLE_ROLES)
              )
                .map((item) => ({ label: t(`form.roles.options.${item}`), value: item }))
                .toArray()}
              placeholder={t('form.roles.placeholder')}
            />
          </Form.Item>
        </Form>
        <Flex justify="end" className="mt-md">
          <Button type="primary" loading={creating} onClick={createManager}>
            {t('buttons.add_manager')}
          </Button>
        </Flex>
      </CollapsibleForm>
      <Flex className="mt-md mb-md">
        <Input.Search
          placeholder={t('search.placeholder')}
          className="max-w-[448px]"
          allowClear
          onChange={(e) => debouncedSetSearch(e.target.value?.trim() ?? '')}
        />
      </Flex>
      <Table
        columns={columns}
        dataSource={managers}
        loading={loading}
        rowKey={(item) => item.id}
        pagination={{
          current: page,
          pageSize: perPage,
          total,
          showSizeChanger: true,
        }}
        onChange={(pagination) => {
          setPage(pagination.current);
          setPerPage(pagination.pageSize);
        }}
        className="rounded-none shadow-card bg-white overflow-hidden"
      />
      {showDeleteManagerModal && (
        <Modal
          open
          title={t('modals.delete_manager.title', { email: selectedManager.email })}
          okButtonProps={{
            danger: true,
            loading: runningActionFor === selectedManager?.id,
          }}
          onOk={() => deleteManager(selectedManager, false)}
          onCancel={() => {
            setShowDeleteManagerModal(false);
            setSelectedManager(null);
          }}
        />
      )}
    </>
  );
}
